import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * ConsentModal — one-time click-through legal acceptance.
 *
 * On mount (post-login only, hence rendered inside AppShell), asks the
 * backend whether the current user has accepted the CURRENT Terms /
 * Privacy / Disclaimer triple. If not, renders a blocking full-viewport
 * overlay with quick summary + links to full pages + "I agree" button.
 *
 * The audit trail (email + versions + timestamp + IP + user-agent) is
 * written to `user_consents` via POST /api/consent/accept.
 */
export default function ConsentModal() {
  const [state, setState] = useState({ loading: true, needs: false, versions: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let dead = false;
    api.get("/consent/status")
      .then(({ data }) => {
        if (dead) return;
        setState({ loading: false, needs: !!data.needs_consent, versions: data.current_versions });
      })
      .catch(() => { if (!dead) setState({ loading: false, needs: false, versions: null }); });
    return () => { dead = true; };
  }, []);

  const accept = async () => {
    if (submitting || !state.versions) return;
    setSubmitting(true);
    try {
      await api.post("/consent/accept", {
        terms_version: state.versions.terms,
        privacy_version: state.versions.privacy,
        disclaimer_version: state.versions.disclaimer,
      });
      toast.success("Thanks — you're all set");
      setState((s) => ({ ...s, needs: false }));
    } catch {
      toast.error("Could not save — please retry");
    } finally { setSubmitting(false); }
  };

  if (state.loading || !state.needs) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-md"
      style={{ background: "rgba(6,8,12,0.82)" }}
      data-testid="consent-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-w-lg w-full border hairline rounded-lg p-6" style={{ background: "var(--surface, #101014)" }}>
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center gap-2">
          <ShieldCheck size={12} style={{ color: "var(--primary)" }} /> One-time consent
        </div>
        <h2 className="display text-2xl font-semibold text-white mb-3">Before you continue</h2>
        <p className="text-sm text-zinc-300 leading-relaxed">
          KOL Radar surfaces algorithmic best-guess estimates from publicly available
          data. It is not advice. By continuing, you confirm you&apos;ve read and agree
          to the current
          {" "}<Link to="/terms" target="_blank" className="text-cyan-400 hover:text-white underline underline-offset-2" data-testid="consent-link-terms">Terms</Link>,
          {" "}<Link to="/privacy" target="_blank" className="text-cyan-400 hover:text-white underline underline-offset-2" data-testid="consent-link-privacy">Privacy Policy</Link>,
          {" "}and{" "}
          <Link to="/disclaimer" target="_blank" className="text-cyan-400 hover:text-white underline underline-offset-2" data-testid="consent-link-disclaimer">Disclaimer</Link>.
        </p>
        <ul className="mt-4 text-[13px] text-zinc-400 space-y-1.5 list-disc pl-5">
          <li>Public data only — no scraping, no insider information.</li>
          <li>Non-exclusive access; nothing here is investment or business advice.</li>
          <li>Don&apos;t scrape, copy, or redistribute the outputs.</li>
        </ul>
        <div className="mt-4 mono text-[10px] text-zinc-600">
          Versions on file · terms {state.versions?.terms} · privacy {state.versions?.privacy} · disclaimer {state.versions?.disclaimer}
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            data-testid="consent-decline"
            onClick={() => { window.location.href = "/api/auth/logout"; }}
            className="px-4 py-2 mono text-xs rounded-md border hairline text-zinc-400 hover:text-white"
          >Decline &amp; sign out</button>
          <button
            data-testid="consent-accept"
            onClick={accept}
            disabled={submitting}
            className="px-4 py-2 mono text-xs rounded-md font-medium disabled:opacity-40 flex items-center gap-1.5"
            style={{ background: "var(--primary)", color: "#0A0A0B" }}
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {submitting ? "Saving…" : "I agree · continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
